import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { 
  BattleRoom, 
  BattleParticipant, 
  BattleResult,
  BattleRoomStatus,
  UserRank,
  RankTier,
  RankDivision,
  RankedMatchResult // eslint-disable-line @typescript-eslint/no-unused-vars
} from '../types/battle';
import { useUser } from './UserContext';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import { generateTestText } from '../utils/textGenerator';
import { firebaseBattleService } from '../utils/firebaseBattle';

// Utility: generate a random 6-char invite code
const generateInviteCode = () => {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
};

// Map Firebase room status to BattleRoomStatus
const mapFirebaseStatus = (s: string): BattleRoomStatus => {
  if (s === 'in-progress') return 'active';
  if (s === 'completed') return 'completed';
  return 'waiting';
};

// Get default user rank (for new users)
const getDefaultRank = (): UserRank => {
  return {
    tier: 'bronze',
    division: 'V',
    points: 0,
    pointsToNext: 100
  };
};

// Calculate rank points based on performance and opponent rank
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const calculateRankPoints = (
  wpm: number, 
  accuracy: number, 
  position: number, 
  totalParticipants: number, 
  userRank: UserRank,
  opponentRanks: UserRank[]
): number => {
  // Base points calculation
  let basePoints = 0;
  
  // Points for finishing position
  if (position === 1) basePoints += 25; // 1st place
  else if (position === 2) basePoints += 15; // 2nd place
  else if (position === 3) basePoints += 10; // 3rd place
  else if (position <= totalParticipants / 2) basePoints += 5; // Top half
  else basePoints -= 5; // Bottom half
  
  // Points based on performance
  const performanceFactor = (wpm * (accuracy / 100)) / 100;
  const performancePoints = Math.round(performanceFactor * 15);
  
  // Rank difference adjustment
  const userRankValue = getRankValue(userRank);
  const avgOpponentRankValue = opponentRanks.reduce((sum, rank) => sum + getRankValue(rank), 0) / opponentRanks.length;
  const rankDiffPoints = Math.round((avgOpponentRankValue - userRankValue) * 0.2);
  
  // Total points
  return basePoints + performancePoints + rankDiffPoints;
};

// Convert rank to numerical value for comparison
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getRankValue = (rank: UserRank): number => {
  const tierValues: Record<RankTier, number> = {
    'bronze': 0,
    'silver': 5,
    'gold': 10,
    'platinum': 15,
    'diamond': 20,
    'master': 25
  };
  
  const divisionValues: Record<RankDivision, number> = {
    'V': 0,
    'IV': 1,
    'III': 2,
    'II': 3,
    'I': 4
  };
  
  return tierValues[rank.tier] + divisionValues[rank.division];
};

// Calculate new rank after a ranked match
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const calculateNewRank = (oldRank: UserRank, pointsGained: number): RankedMatchResult => { // NOSONAR - cognitive complexity required by rank algorithm
  let newRank: UserRank = { ...oldRank };
  let promotedTier = false;
  let promotedDivision = false;
  let demotedTier = false;
  let demotedDivision = false;
  
  // Update points
  newRank.points += pointsGained;
  
  // Handle promotion
  if (newRank.points >= newRank.pointsToNext) {
    // Move to next division or tier
    if (newRank.division === 'I') {
      // Promote to next tier
      const nextTier = getNextTier(newRank.tier);
      if (nextTier) {
        newRank.tier = nextTier;
        newRank.division = 'V';
        promotedTier = true;
      }
    } else {
      // Promote to next division
      newRank.division = getNextDivision(newRank.division);
      promotedDivision = true;
    }
    
    // Reset points after promotion
    newRank.points = newRank.points - newRank.pointsToNext;
    newRank.pointsToNext = getPointsToNextRank(newRank.tier, newRank.division);
  }
  
  // Handle demotion (only if lost points)
  if (pointsGained < 0 && newRank.points < 0) {
    if (newRank.division === 'V') {
      // Can only demote tier if not in bronze
      if (newRank.tier === 'bronze') {
        // Can't go lower than Bronze V
        newRank.points = 0;
      } else {
        const prevTier = getPrevTier(newRank.tier);
        newRank.tier = prevTier;
        newRank.division = 'I';
        demotedTier = true;
      }
    } else {
      // Demote division
      newRank.division = getPrevDivision(newRank.division);
      demotedDivision = true;
    }
    
    // If demoted, set points
    if (demotedTier || demotedDivision) {
      newRank.pointsToNext = getPointsToNextRank(newRank.tier, newRank.division);
      newRank.points = Math.floor(newRank.pointsToNext * 0.66); // Start at 2/3 of the way to next rank
    }
  }
  
  return {
    oldRank,
    newRank,
    pointsGained,
    promotedTier,
    promotedDivision,
    demotedTier,
    demotedDivision
  };
};

// Helper functions for rank calculations
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getNextTier = (tier: RankTier): RankTier | null => {
  const tiers: RankTier[] = ['bronze', 'silver', 'gold', 'platinum', 'diamond', 'master'];
  const currentIndex = tiers.indexOf(tier);
  return currentIndex < tiers.length - 1 ? tiers[currentIndex + 1] : null;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getPrevTier = (tier: RankTier): RankTier => {
  const tiers: RankTier[] = ['bronze', 'silver', 'gold', 'platinum', 'diamond', 'master'];
  const currentIndex = tiers.indexOf(tier);
  return currentIndex > 0 ? tiers[currentIndex - 1] : 'bronze';
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getNextDivision = (division: RankDivision): RankDivision => {
  const divisions: RankDivision[] = ['V', 'IV', 'III', 'II', 'I'];
  const currentIndex = divisions.indexOf(division);
  return currentIndex < divisions.length - 1 ? divisions[currentIndex + 1] : 'I';
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getPrevDivision = (division: RankDivision): RankDivision => {
  const divisions: RankDivision[] = ['V', 'IV', 'III', 'II', 'I'];
  const currentIndex = divisions.indexOf(division);
  return currentIndex > 0 ? divisions[currentIndex - 1] : 'V';
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getPointsToNextRank = (tier: RankTier, division: RankDivision): number => {
  // Higher tiers and divisions require more points
  const basePoints = 100;
  
  const tierMultiplier: Record<RankTier, number> = {
    'bronze': 1,
    'silver': 1.2,
    'gold': 1.4,
    'platinum': 1.6,
    'diamond': 1.8,
    'master': 2
  };
  
  const divisionMultiplier: Record<RankDivision, number> = {
    'V': 1,
    'IV': 1.1,
    'III': 1.2,
    'II': 1.3,
    'I': 1.4
  };
  
  return Math.round(basePoints * tierMultiplier[tier] * divisionMultiplier[division]);
};

// Types
interface BattleState {
  activeRoom: BattleRoom | null;
  availableRooms: BattleRoom[];
  pastResults: BattleResult[];
  isLoading: boolean;
  isSearchingRanked: boolean;
  error: string | null;
}

type BattleAction =
  | { type: 'CREATE_ROOM_START' }
  | { type: 'CREATE_ROOM_SUCCESS'; payload: BattleRoom }
  | { type: 'CREATE_ROOM_ERROR'; payload: string }
  | { type: 'JOIN_ROOM_START' }
  | { type: 'JOIN_ROOM_SUCCESS'; payload: BattleRoom }
  | { type: 'JOIN_ROOM_ERROR'; payload: string }
  | { type: 'UPDATE_ROOM'; payload: BattleRoom }
  | { type: 'LEAVE_ROOM' }
  | { type: 'START_BATTLE' }
  | { type: 'END_BATTLE'; payload: BattleResult }
  | { type: 'UPDATE_PARTICIPANT'; payload: BattleParticipant }
  | { type: 'FETCH_ROOMS_SUCCESS'; payload: BattleRoom[] }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'START_RANKED_SEARCH' }
  | { type: 'STOP_RANKED_SEARCH' };

interface BattleContextType {
  state: BattleState;
  createRoom: (roomSettings: Omit<BattleRoom, 'id' | 'inviteCode' | 'hostId' | 'participants' | 'status' | 'text' | 'createdAt' | 'startedAt' | 'endedAt'>) => Promise<BattleRoom>;
  joinRoom: (roomId: string, isInviteCode?: boolean) => Promise<void>;
  leaveRoom: () => void;
  startBattle: () => Promise<void>;
  updateParticipant: (participantUpdate: Partial<BattleParticipant>) => Promise<void>;
  toggleReady: () => Promise<void>;
  fetchPublicRooms: () => Promise<void>;
  startRankedSearch: () => void;
  stopRankedSearch: () => void;
  getUserRank: () => UserRank;
}

// Initial state
const initialState: BattleState = {
  activeRoom: null,
  availableRooms: [],
  pastResults: [],
  isLoading: false,
  isSearchingRanked: false,
  error: null,
};

// Reducer
const battleReducer = (state: BattleState, action: BattleAction): BattleState => {
  switch (action.type) {
    case 'CREATE_ROOM_START':
    case 'JOIN_ROOM_START':
      return { ...state, isLoading: true, error: null };
      
    case 'CREATE_ROOM_SUCCESS':
    case 'JOIN_ROOM_SUCCESS':
      return {
        ...state,
        activeRoom: action.payload,
        isLoading: false,
        error: null,
      };
      
    case 'CREATE_ROOM_ERROR':
    case 'JOIN_ROOM_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
      
    case 'UPDATE_ROOM':
      return {
        ...state,
        activeRoom: action.payload,
      };
      
    case 'LEAVE_ROOM':
      return {
        ...state,
        activeRoom: null,
      };
      
    case 'START_BATTLE':
      if (!state.activeRoom) return state;
      return {
        ...state,
        activeRoom: {
          ...state.activeRoom,
          status: 'countdown',
          startedAt: Date.now(),
        },
      };
      
    case 'END_BATTLE':
      return {
        ...state,
        activeRoom: state.activeRoom ? {
          ...state.activeRoom,
          status: 'completed',
          endedAt: Date.now(),
        } : null,
        pastResults: [...state.pastResults, action.payload],
      };
      
    case 'UPDATE_PARTICIPANT':
      if (!state.activeRoom) return state;
      return {
        ...state,
        activeRoom: {
          ...state.activeRoom,
          participants: state.activeRoom.participants.map(p => 
            p.userId === action.payload.userId ? { ...p, ...action.payload } : p
          ),
        },
      };
      
    case 'FETCH_ROOMS_SUCCESS':
      return {
        ...state,
        availableRooms: action.payload,
        isLoading: false,
      };
      
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
      
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
      
    case 'START_RANKED_SEARCH':
      return {
        ...state,
        isSearchingRanked: true,
        error: null,
      };
      
    case 'STOP_RANKED_SEARCH':
      return {
        ...state,
        isSearchingRanked: false,
        error: null,
      };
      
    default:
      return state;
  }
};

// Create context
const BattleContext = createContext<BattleContextType | undefined>(undefined);

// Provider component
export const BattleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(battleReducer, initialState);
  const { state: userState } = useUser();

  // Initialize Firebase battle service when user logs in
  useEffect(() => {
    if (userState.user?.id) {
      firebaseBattleService.init(userState.user.id).catch(() => {});
    }
  }, [userState.user?.id]);
  
  // Create a new battle room
  const createRoom = React.useCallback(async (roomSettings: Omit<BattleRoom, 'id' | 'inviteCode' | 'hostId' | 'participants' | 'status' | 'text' | 'createdAt' | 'startedAt' | 'endedAt'>): Promise<BattleRoom> => {
    if (!userState.user) {
      throw new Error('User must be logged in to create a room');
    }
    
    dispatch({ type: 'CREATE_ROOM_START' });
    
    try {
      // In a real app, make an API call here
      const inviteCode = generateInviteCode();
      
      const newRoom: BattleRoom = {
        id: uuidv4(),
        inviteCode,
        hostId: userState.user.id,
        name: roomSettings.name,
        isPublic: roomSettings.isPublic,
        settings: roomSettings.settings,
        participants: [{
          userId: userState.user.id,
          username: userState.user.username || 'Anonymous',
          avatar: userState.user.avatar,
          isHost: true,
          isReady: true, // Host is always ready
          progress: 0,
          position: 1,
          stats: {
            wpm: 0,
            accuracy: 100,
            errors: 0,
            charsTyped: 0,
            wordsTyped: 0,
            correctChars: 0,
            totalChars: 0
          }
        }],
        status: 'waiting',
        text: generateTestText({
          mode: roomSettings.settings.mode,
          duration: roomSettings.settings.duration,
          wordCount: roomSettings.settings.wordCount,
          punctuation: roomSettings.settings.includePunctuation,
          numbers: roomSettings.settings.includeNumbers
        }).text,
        maxParticipants: roomSettings.settings.maxParticipants,
        createdAt: Date.now(),
      };
      
      dispatch({ type: 'CREATE_ROOM_SUCCESS', payload: newRoom });
      toast.success('Battle room created successfully!');
      return newRoom;
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create room';
      dispatch({ type: 'CREATE_ROOM_ERROR', payload: errorMessage });
      toast.error('Failed to create battle room');
      throw error;
    }
  }, [userState.user]);
  
  // Join an existing battle room
  const joinRoom = React.useCallback(async (roomIdOrCode: string, isInviteCode = false): Promise<void> => {
    if (!userState.user) {
      toast.error('You must be logged in to join a battle');
      throw new Error('User must be logged in to join a room');
    }
    
    dispatch({ type: 'JOIN_ROOM_START' });
    
    try {
      // In a real app, make an API call here
      
      // Find the room (simulated)
      let targetRoom: BattleRoom;
      
      if (isInviteCode) {
        const roomByCode = state.availableRooms.find(r => r.inviteCode === roomIdOrCode);
        if (!roomByCode) {
          throw new Error(`Room with invite code ${roomIdOrCode} not found`);
        }
        targetRoom = roomByCode;
      } else {
        const roomById = state.availableRooms.find(r => r.id === roomIdOrCode);
        if (!roomById) {
          throw new Error(`Room with ID ${roomIdOrCode} not found`);
        }
        targetRoom = roomById;
      }
      
      // Check if room is full
      if (targetRoom.participants.length >= targetRoom.maxParticipants) {
        throw new Error('This room is full');
      }
      
      // Check if user is already in the room
      if (targetRoom.participants.some(p => p.userId === userState.user?.id)) {
        dispatch({ type: 'JOIN_ROOM_SUCCESS', payload: targetRoom });
        return;
      }
      
      // Add user to participants
      const updatedRoom: BattleRoom = {
        ...targetRoom,
        participants: [
          ...targetRoom.participants,
          {
            userId: userState.user.id,
            username: userState.user.username || 'Anonymous',
            avatar: userState.user.avatar,
            isHost: false,
            isReady: false,
            progress: 0,
            position: targetRoom.participants.length + 1,
            stats: {
              wpm: 0,
              accuracy: 100,
              errors: 0,
              charsTyped: 0,
              wordsTyped: 0,
              correctChars: 0,
              totalChars: 0
            }
          }
        ]
      };
      
      dispatch({ type: 'JOIN_ROOM_SUCCESS', payload: updatedRoom });
      toast.success(`Joined ${targetRoom.name}`);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to join room';
      dispatch({ type: 'JOIN_ROOM_ERROR', payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  }, [userState.user, state.availableRooms]);
  
  // Leave current battle room
  const leaveRoom = React.useCallback(() => {
    if (!state.activeRoom || !userState.user) return;
    
    // If user is host, close the room or transfer host
    if (state.activeRoom.hostId === userState.user.id) {
      // In a real app, make an API call here to close the room
      toast('Room closed as you were the host', { 
        icon: '📢',
        style: { background: '#3b82f6', color: 'white' }
      });
    } else {
      // In a real app, make an API call here to remove participant
      toast('Left the battle room', { 
        icon: '👋',
        style: { background: '#3b82f6', color: 'white' } 
      });
    }
    
    dispatch({ type: 'LEAVE_ROOM' });
  }, [state.activeRoom, userState.user]);
  
  // Start the battle (host only)
  const startBattle = React.useCallback(async (): Promise<void> => {
    if (!state.activeRoom || !userState.user) {
      toast.error('No active room or user not logged in');
      throw new Error('No active room or user not logged in');
    }
    
    if (state.activeRoom.hostId !== userState.user.id) {
      toast.error('Only the host can start the battle');
      throw new Error('Only the host can start the battle');
    }
    
    try {
      // In a real app, make an API call here
      dispatch({ type: 'START_BATTLE' });
      
      // Simulate countdown and then set to active
      setTimeout(() => {
        if (state.activeRoom) {
          dispatch({ 
            type: 'UPDATE_ROOM', 
            payload: { ...state.activeRoom, status: 'active' } 
          });
        }
      }, 3000); // 3 seconds countdown
      
      toast.success('Battle started!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start battle';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  }, [state.activeRoom, userState.user]);
  
  // Update participant stats during battle
  const updateParticipant = React.useCallback(async (participantUpdate: Partial<BattleParticipant>): Promise<void> => {
    if (!state.activeRoom || !userState.user) return;
    
    try {
      // In a real app, make an API call here
      const currentParticipant = state.activeRoom.participants.find(
        p => p.userId === userState.user?.id
      );
      
      if (!currentParticipant) return;
      
      const updatedParticipant = {
        ...currentParticipant,
        ...participantUpdate,
      };
      
      dispatch({ type: 'UPDATE_PARTICIPANT', payload: updatedParticipant });
      
      // If participant completed the test and room is in active state
      if (updatedParticipant.progress === 100 && state.activeRoom.status === 'active') {
        // In time mode, player can finish early
        // In word mode, this means they've completed all words
        
        // Update completion time if not set yet
        if (!updatedParticipant.stats.completedAt) {
          dispatch({
            type: 'UPDATE_PARTICIPANT',
            payload: {
              ...updatedParticipant,
              stats: {
                ...updatedParticipant.stats,
                completedAt: Date.now(),
              }
            }
          });
        }
        
        // If all participants have finished, end the battle
        const allFinished = state.activeRoom.participants.every(p => 
          p.progress === 100 || p.userId === userState.user?.id
        );
        
        if (allFinished) {
          // End the battle
          const result: BattleResult = {
            roomId: state.activeRoom.id,
            text: state.activeRoom.text,
            participants: state.activeRoom.participants,
            mode: state.activeRoom.settings.mode,
            startTime: state.activeRoom.startedAt || Date.now(),
            endTime: Date.now(),
            duration: state.activeRoom.settings.duration || 0,
            wordCount: state.activeRoom.settings.wordCount || 0,
          };
          
          dispatch({ type: 'END_BATTLE', payload: result });
        }
      }
    } catch (error) {
      console.error('Error updating participant:', error);
    }
  }, [state.activeRoom, userState.user]);
  
  // Toggle ready status (non-host only)
  const toggleReady = React.useCallback(async (): Promise<void> => {
    if (!state.activeRoom || !userState.user) return;
    
    try {
      // Find current participant
      const currentParticipant = state.activeRoom.participants.find(
        p => p.userId === userState.user?.id
      );
      
      if (!currentParticipant || currentParticipant.isHost) return;
      
      // Update ready status
      dispatch({
        type: 'UPDATE_PARTICIPANT',
        payload: {
          ...currentParticipant,
          isReady: !currentParticipant.isReady,
        }
      });
    } catch (error) {
      console.error('Error toggling ready status:', error);
    }
  }, [state.activeRoom, userState.user]);
  
  // Fetch available public rooms from Firebase
  const fetchPublicRooms = React.useCallback(async (): Promise<void> => {
    try {
      const firebaseRooms = await firebaseBattleService.getPublicBattleRooms();

      // Map Firebase rooms to our BattleRoom type
      const rooms: BattleRoom[] = firebaseRooms.map((fr) => ({
        id: fr.id,
        name: fr.name,
        isPublic: !fr.isPrivate,
        inviteCode: fr.id.substring(0, 6).toUpperCase(),
        hostId: fr.creatorId,
        settings: {
          mode: 'time',
          duration: 60,
          wordCount: 50,
          includePunctuation: false,
          includeNumbers: false,
          maxParticipants: fr.maxPlayers,
          isPublic: !fr.isPrivate,
        },
        participants: fr.players.map((p, idx) => ({
          userId: p.id,
          username: p.username,
          isHost: p.id === fr.creatorId,
          isReady: p.status === 'ready',
          progress: p.progress,
          position: idx + 1,
          stats: {
            wpm: p.wpm,
            accuracy: p.accuracy,
            errors: 0,
            charsTyped: 0,
            wordsTyped: 0,
            correctChars: 0,
            totalChars: 0,
          },
        })),
        status: mapFirebaseStatus(fr.status),
        text: fr.text || '',
        maxParticipants: fr.maxPlayers,
        createdAt: fr.createdAt?.toMillis ? fr.createdAt.toMillis() : Date.now(),
      }));

      dispatch({ type: 'FETCH_ROOMS_SUCCESS', payload: rooms });
    } catch (error) {
      console.error('Error fetching public rooms:', error);
      dispatch({ type: 'FETCH_ROOMS_SUCCESS', payload: [] });
    }
  }, []);
  
  // Context value with useMemo to prevent recreation on every render
  const value: BattleContextType = React.useMemo(() => ({
    state,
    createRoom,
    joinRoom,
    leaveRoom,
    startBattle,
    updateParticipant,
    toggleReady,
    fetchPublicRooms,
    startRankedSearch: () => dispatch({ type: 'START_RANKED_SEARCH' }),
    stopRankedSearch: () => dispatch({ type: 'STOP_RANKED_SEARCH' }),
    getUserRank: () => {
      if (!userState.user) return getDefaultRank();
      return userState.user.rank || getDefaultRank();
    }
  }), [state, createRoom, joinRoom, leaveRoom, startBattle, updateParticipant, toggleReady, fetchPublicRooms, userState.user]);
  
  return <BattleContext.Provider value={value}>{children}</BattleContext.Provider>;
};

// Custom hook for using the battle context
export const useBattle = () => {
  const context = useContext(BattleContext);
  
  if (context === undefined) {
    throw new Error('useBattle must be used within a BattleProvider');
  }
  
  return context;
};
